import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { TimeSessionJSON } from "@/types/models";

interface stopwatchState {
  time: number;
  isRunning: boolean;
  // Name of the in-progress session. Lives in the slice (not component
  // state) so it survives dialog close and is available to the
  // auto-save effects.
  activeLabel: string;
  // Which reading the running clock belongs to — used to flush the
  // in-progress session to the right reading when the selection
  // changes mid-run.
  activeReadingId: string | null;
  // Logged sessions for the reading the timer dialog is showing.
  // Cached on dialog open and refreshed after each finalize; keyed by
  // reading id so a stale list is never rendered against a different
  // reading. Persisted per (user, reading) server-side.
  sessions: TimeSessionJSON[];
  sessionsReadingId: string | null;
}

const initialState: stopwatchState = {
  time: 0,
  isRunning: false,
  activeLabel: "",
  activeReadingId: null,
  sessions: [],
  sessionsReadingId: null,
};

export const stopwatchSlice = createSlice({
  name: "stopwatch",
  initialState,
  reducers: {
    setTime: (state) => {
      state.time = state.time + 1;
    },
    setisRunning: (state) => {
      state.isRunning = !state.isRunning;
    },
    reset: (state) => {
      state.time = 0;
      state.isRunning = false;
    },
    // Zero the clock without stopping it — the "new session" rollover,
    // where timing continues seamlessly into the next session.
    clearClock: (state) => {
      state.time = 0;
    },
    setActiveLabel: (state, action: PayloadAction<string>) => {
      state.activeLabel = action.payload;
    },
    setActiveReadingId: (state, action: PayloadAction<string | null>) => {
      state.activeReadingId = action.payload;
    },
    // Restore a server-saved in-progress session (e.g. returning to a
    // reading, or reopening the app).
    hydrateActive: (
      state,
      action: PayloadAction<{
        readingId: string;
        label: string;
        seconds: number;
      }>
    ) => {
      state.activeReadingId = action.payload.readingId;
      state.activeLabel = action.payload.label;
      state.time = action.payload.seconds;
    },
    setSessions: (
      state,
      action: PayloadAction<{
        readingId: string;
        sessions: TimeSessionJSON[];
      }>
    ) => {
      state.sessionsReadingId = action.payload.readingId;
      state.sessions = action.payload.sessions;
    },
  },
});

export const {
  setTime,
  setisRunning,
  reset,
  clearClock,
  setActiveLabel,
  setActiveReadingId,
  hydrateActive,
  setSessions,
} = stopwatchSlice.actions;

export default stopwatchSlice.reducer;
